import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Callout from 'components/misc/Callout';
import Card from 'components/core/Card';
import CountryFieldSelector from 'components/inputFields/CountryFieldSelector';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
// Custom
import { cpmInvoiceDataShape, errorShape } from 'constants/PropTypesShapes';
import { buildFormErrors, buildGlobalErrors, cpmAddressConstraints } from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';

class InvoiceEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        companyName,
        city,
        country,
        extra,
        nationalIdentifier,
        street,
        zipCode,
      },
      invoice,
      onErrorsChange,
      onValidate,
    } = this.props;

    // Build errors
    let errors = null;

    // Require address if at least one field set
    if (city || country || extra || street || zipCode) {
      errors = buildFormErrors(
        {
          city,
          country,
          extra,
          street,
          zipCode,
        },
        cpmAddressConstraints,
      );
    }

    // Add an error if there are no changes; rebuild global error as errors is empty
    // Check only the address's city as all fields are required if one is set
    if (!errors && !(companyName || city || nationalIdentifier)) {
      errors = buildGlobalErrors('updateCarparkInvoice.empty');
    }

    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      // Check only the address's city as all fields are required if one is set
      let data = { companyName, nationalIdentifier };
      if (city) {
        data = {
          ...data,
          address: {
            city,
            country,
            extra,
            street,
            zipCode,
          },
        };
      }
      onValidate(invoice, data);

      return true;
    }

    return false;
  }

  render() {
    const {
      fields: {
        city,
        companyName,
        country,
        extra,
        nationalIdentifier,
        street,
        zipCode,
      },
      errors,
      onFieldsChange,
    } = this.props;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12 pb-2">
            <Callout severity="warning" withInfoIcon>
              <LocalizedMessage id="help.invoice.updateOnlyOnce" />
            </Callout>
          </div>
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="title.invoiceUpdateAddress" />,
                    content: '',
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="title.invoiceUpdateAddress" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    <div className="col-12 col-lg-9">
                      <TextField
                        name="street"
                        label={<LocalizedMessage id="field.address" />}
                        errorText={_get(errors, 'street') || _get(errors, 'address.street')}
                        value={street}
                        onChange={(value) => onFieldsChange({ street: value })}
                      />
                      <TextField
                        name="extra"
                        label={<LocalizedMessage id="field.addressAdditional" />}
                        errorText={_get(errors, 'street') || _get(errors, 'address.street')}
                        value={extra}
                        onChange={(value) => onFieldsChange({ extra: value })}
                      />
                      <TextField
                        name="zipCode"
                        label={<LocalizedMessage id="field.zipcode" />}
                        errorText={_get(errors, 'zipCode') || _get(errors, 'address.zipCode')}
                        value={zipCode}
                        onChange={(value) => onFieldsChange({ zipCode: value })}
                      />
                      <TextField
                        name="city"
                        label={<LocalizedMessage id="field.city" />}
                        errorText={_get(errors, 'city') || _get(errors, 'address.city')}
                        value={city}
                        onChange={(value) => onFieldsChange({ city: value })}
                      />
                      <CountryFieldSelector
                        countriesScope="known"
                        value={country}
                        errorText={_get(errors, 'country') || _get(errors, 'address.country')}
                        onChange={(value) => onFieldsChange({ country: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            <div className="d-flex pt-3">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="title.invoiceUpdateCompany" />,
                    content: '',
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="title.invoiceUpdateCompany" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    <div className="col-12 col-lg-9">
                      <TextField
                        name="companyName"
                        label={<LocalizedMessage id="field.name" />}
                        errorText={_get(errors, 'companyName')}
                        value={companyName}
                        onChange={(value) => onFieldsChange({ companyName: value })}
                      />
                      <TextField
                        name="nationalIdentifier"
                        label={<LocalizedMessage id="field.nationalIdentifier" />}
                        errorText={_get(errors, 'nationalIdentifier')}
                        value={nationalIdentifier}
                        onChange={(value) => onFieldsChange({ nationalIdentifier: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

InvoiceEditForm.propTypes = {
  errors: PropTypes.object,
  fields: PropTypes.object.isRequired,
  invoice: cpmInvoiceDataShape.isRequired,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
};

InvoiceEditForm.defaultProps = {
  errors: null,
  operationError: null,
};

const mapFieldsToProps = {
  city: {
    defaultValue: '',
  },
  companyName: {
    defaultValue: '',
  },
  country: {
    defaultValue: null,
  },
  extra: {
    defaultValue: '',
  },
  nationalIdentifier: {
    defaultValue: '',
  },
  street: {
    defaultValue: '',
  },
  zipCode: {
    defaultValue: '',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: ownProps.operationError,
});

const mapStateToProps = (state) => ({
  operationError: state.cpmCarparkInvoice.update.error,
});

module.exports = compose(
  connect(mapStateToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['updateCarparkInvoice']),
)(InvoiceEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/payments/InvoiceEditForm.js